/**
 * @file counters-form.ts
 * @description Чистые правила панели «Весь сайт → Счётчики и cookie»
 * (phase19.16, партия Р3г, v0.19.62; site-builder.md SB133, SB134, SB137;
 * макет uiux-r3.md §3.6 с исправлениями §7.29: номер VK — числовой ID
 * пикселя VK Рекламы, владелец правит только вступление, вид по умолчанию —
 * полоса; подсказки — integrations-r3g-counters.md §6).
 *
 * Формат номеров и умолчания — из shared (counters.ts, consent-banner.ts):
 * кабинет их не дублирует, здесь только чтение черновика, разбор ввода и
 * подписи групп. Тексты платформы (редакции), «что увидит посетитель» и вид
 * канвы — consent-platform.ts.
 *
 * «SEO-техника» v0.19.65 (SB147; uiux-seo-tech §3.5; требование
 * пользователя 2026-10-25 «Добавь рекомендации в настройках»): галочка
 * «Передавать данные о товарах и заказах» и подсказки о действиях во
 * внешних кабинетах — в Метрике (включить «Электронную коммерцию») и VK
 * Рекламе (JS-событие `purchase`), напоминание об абзаце политики ПДн.
 */

import {
  COUNTER_SERVICES,
  DEFAULT_SITE_CONSENT_BANNER,
  SITE_CONSENT_BANNER_SIDE_LABELS,
  SITE_COUNTER_MODE_LABELS,
  SITE_LIMITS,
  consentIntroIssue,
  counterIdError,
  effectiveCounterMode,
  enabledCounterServices,
  metrikaEcommerceEnabled,
  normalizeCounterId,
  readCounters,
  siteConsentBannerKind,
  type SiteConsentBanner,
  type SiteCounterMode,
  type SiteCounterService,
  type SiteCounters,
  type SiteTheme,
} from "@chaiberi/shared";

import type { SiteViewDraft } from "./types";

// ── Чтение черновика ──

/** Часть COUNTERS черновика; сервер до Р3г поля не отдаёт — умолчания shared. */
export function countersOf(draft: Pick<SiteViewDraft, "counters">): SiteCounters {
  return readCounters(draft.counters).counters;
}

/** Вид баннера из темы; у документа без раздела — нынешняя полоса. */
export function consentBannerOf(theme: SiteTheme): SiteConsentBanner {
  const banner: SiteConsentBanner | undefined = (theme as Partial<Pick<SiteTheme, "consentBanner">>).consentBanner;
  return banner ?? DEFAULT_SITE_CONSENT_BANNER;
}

// ── Ввод номера счётчика ──

/** Разбор поля номера: пусто — сервис выключен (`id: null`), верный номер — запись, иначе ошибка. */
export type CounterIdInput = { ok: true; id: string | null } | { ok: false; error: string };

export function parseCounterIdInput(service: SiteCounterService, raw: string): CounterIdInput {
  const id = normalizeCounterId(raw);
  if (id === "") return { ok: true, id: null };
  const error = counterIdError(service, id);
  return error === null ? { ok: true, id } : { ok: false, error };
}

/** Подпись, подсказка «где взять номер» и пример — реестром по сервисам (OCP). */
export const COUNTER_FIELD_TEXTS: Readonly<Record<SiteCounterService, { label: string; hint: string; placeholder: string }>> = {
  metrika: {
    label: `${COUNTER_SERVICES.metrika.label} — номер счётчика`,
    hint: "metrika.yandex.ru → ваш счётчик: номер — цифры рядом с названием. Код на сайт вставлять не нужно — его ставит платформа. Пусто — Метрика выключена.",
    placeholder: "12345678",
  },
  vk: {
    label: `${COUNTER_SERVICES.vk.label} — номер пикселя`,
    hint: "ads.vk.com → «Конверсии» → «Пиксели» → числовой ID пикселя. Код вставлять не нужно. Пусто — VK Реклама выключена.",
    placeholder: "36383",
  },
};

export const WEBVISOR_HINT =
  "Запись действий посетителей на страницах. На оформлении заказа и в личном кабинете не пишет никогда. " +
  "Включите Вебвизор и в Метрике: «Настройка → Вебвизор», там же выключите «Записывать все поля». " +
  "Включение спросит согласие посетителей заново.";

// ── Электронная коммерция (SB147) ──

export const ECOMMERCE_LABEL = "Передавать данные о товарах и заказах (электронная коммерция)";

export const ECOMMERCE_HINT =
  "Просмотры товаров, корзина, состав и сумма заказа — без имени, телефона, email и промокода. " +
  "Метрика получит их только после согласия посетителя на аналитику. Изменение спросит согласие посетителей заново.";

/** Что сделать в самой Метрике (integrations-seo-tech §6). */
export const ECOMMERCE_METRIKA_STEPS =
  "В Метрике: «Настройка» → «Электронная коммерция» → включить, контейнер dataLayer. Данные в отчёте появляются через несколько часов.";

export const ECOMMERCE_NO_METRIKA = "Сначала укажите номер счётчика Метрики.";

/** Напоминание о политике ПДн (текст абзаца — юрист, OQ-123). */
export const ECOMMERCE_POLICY_NOTE = "Добавьте в политику ПДн, что Метрика получает состав и сумму заказов.";

/** Что получит VK и что сделать в VK Рекламе (SB147; integrations-seo-tech §6). */
export const VK_PURCHASE_HINT =
  "VK получит только цель «Покупка» с суммой заказа — после согласия на рекламу. " +
  "В VK Рекламе: «Конверсии» → «Пиксели» → «События» → добавить JS-событие purchase, категория «Покупка» — до выпуска.";

/**
 * Метрика с новыми полями: раскрываем прежнее значение (`...metrika`), чтобы
 * смена номера или Вебвизора не теряла галочку электронной коммерции
 * (находка uiux-seo-tech §7 п. 1). Новая Метрика — Вебвизор выключен,
 * электронная коммерция включена (умолчания SB133, SB147).
 */
export function metrikaWith(metrika: SiteCounters["metrika"], patch: { id?: string; webvisor?: boolean; ecommerce?: boolean }): NonNullable<SiteCounters["metrika"]> | null {
  const id = patch.id ?? metrika?.id;
  if (!id) return null;
  return { webvisor: false, ecommerce: true, ...metrika, ...patch, id };
}

export const NO_GA_TEXT = "Google Analytics не подключаем: данные посетителей уходили бы за рубеж (152-ФЗ).";

// ── Режим согласия ──

export const MODE_HINTS: Readonly<Record<SiteCounterMode, string>> = {
  consent: "Рекомендуем. Метрика и VK загружаются, только когда посетитель нажал «Принять все» или включил их в «Настроить».",
  notice: "Счётчики работают сразу, в баннере есть «Отказаться». Юрист ещё не ответил, достаточно ли этого; риск — на магазине.",
};

/** Подпись у заблокированного варианта (SB133). */
export const NOTICE_FORBIDDEN_TEXT = "Платформа запретила режим «Уведомление»";

/** Черновик уже в «Уведомлении», а платформа его запретила: сайт работает «после согласия», сохранить нельзя. */
export const NOTICE_FORBIDDEN_DRAFT_TEXT =
  "Платформа запретила режим «Уведомление» — счётчики включатся только после согласия посетителя. " +
  "Пока выбран этот режим, черновик не сохранится.";

export const WITHOUT_COUNTERS_HINT = "Короткая полоса «Мы используем cookie для работы корзины и личного кабинета» с кнопкой «Понятно» — как сейчас.";

// ── Вступление ──

/** Ошибка вступления словами владельца или null. */
export function introError(intro: string): string | null {
  const issue = consentIntroIssue(intro);
  if (issue === "too_long") return `Не длиннее ${SITE_LIMITS.consentIntro} знаков.`;
  if (issue === "markup") return "Только простой текст: без ссылок, адресов сайтов и угловых скобок.";
  return null;
}

export const INTRO_PLATFORM_TEXT =
  "Фразу о режиме, список сервисов и кнопки «Принять все / Только необходимые / Настроить» ставит платформа — их не изменить.";

// ── Сводки групп (видны у свёрнутой группы) ──

export function servicesSummary(counters: SiteCounters): string {
  const on = enabledCounterServices(counters);
  if (on.length === 0) return "не подключены";
  // «Метрика + эл. коммерция, VK» (uiux-seo-tech §3.5).
  return on.map((s) => (s === "metrika" && metrikaEcommerceEnabled(counters) ? `${COUNTER_SERVICES[s].shortLabel} + эл. коммерция` : COUNTER_SERVICES[s].shortLabel)).join(", ");
}

export function modeSummary(counters: SiteCounters, noticeAllowed: boolean): string {
  return SITE_COUNTER_MODE_LABELS[effectiveCounterMode(counters, noticeAllowed)].toLowerCase();
}

export function bannerSummary(banner: SiteConsentBanner, counters: SiteCounters, noticeAllowed: boolean): string {
  if (siteConsentBannerKind(counters, noticeAllowed) === null) return "не показывается";
  if (banner.layout === "bar") return "полоса внизу";
  return `карточка ${banner.cardWidth} px, ${SITE_CONSENT_BANNER_SIDE_LABELS[banner.cardSide].toLowerCase()}`;
}

export function textsSummary(counters: SiteCounters): string {
  return counters.intro.trim() === "" ? "как у платформы" : "своё вступление";
}
